import { useEffect, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Link, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackSidebarMatterRecordOpened } from '@/analytics/utils/matter-record-analytics'
import { trackSidebarLinkClicked } from '@/analytics/utils/sidebar-analytics'
import { Clipboard, Plus } from '@/assets/icons'
import { BuildMatterRecordModal } from '@/matter-asset-common/selection/BuildMatterRecordModal'
import { MenuContent, MenuItemGroup } from '@/components/menu/MenuContent'
import { Tag } from '@/components/tag/Tag'
import { useBuildMatterRecordModal } from '@/routes/dashboard/sidebar/hooks/useBuildMatterRecordModal'
import { SideNavRailItem } from '../side-nav-item/SideNavRailItem'
import { getSideNavItemStateClassNames } from '../side-nav-item/side-nav-item-state-styles'
import { useMatterRecordsItems } from './hooks/useMatterRecordsItems'

export type SideNavRailMatterRecordProps = {
  matterId: string
  /** ID of the currently active item for highlight state */
  activeItemId?: string
  /** Called after an item is clicked */
  onNavigate?: () => void
}

/* The menu item geometry the flyout keeps, so its rows stay sized like the menu they sit in. */
const kFlyoutItemLayout = clsx(
  'group flex w-full cursor-pointer items-center gap-2.5 rounded p-2 text-left text-sm font-medium tracking-[-0.15px]',
  // The keyboard cue, and the only one on an arrowed-to active row, whose background already
  // carries the highlight colour. A menu moves focus onto whichever row the pointer reaches, so
  // the ring is suppressed under the pointer to keep it from reading as a hover state.
  'focus:outline-hidden focus-visible:outline-hidden',
  '[&:focus-visible:not(:hover)]:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]'
)

// Inactive item styling used by the "Build a new record" action row.
const kInactiveItemStyle = getSideNavItemStateClassNames(false)

/**
 * The contracted rail's "Matter Records" flyout.
 *
 * Self-contained: owns item data via `useMatterRecordsItems` and modal state via
 * `useBuildMatterRecordModal`. The rail's own `BuildMatterRecordModal` instance is
 * independent of the expanded nav's — both are mounted simultaneously but only one is
 * visible at a time via CSS opacity.
 *
 * Built on Radix `DropdownMenu`, which supplies outside-click dismissal, Escape, roving-focus
 * keyboard navigation, and viewport collision avoidance. It also portals, so the rail's
 * `overflow-y-auto` cannot clip the flyout.
 *
 * Items are real `Link`s (via `asChild`) rather than `MenuItem`, which renders a div and would
 * lose the cmd-click / open-in-new-tab the expanded nav's items have. They keep the menu's geometry
 * but take their state colours from the expanded nav, so an item reads the same on both surfaces.
 */
export function SideNavRailMatterRecord({ matterId, activeItemId, onNavigate }: SideNavRailMatterRecordProps) {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()

  const { trackEvent } = useAnalytics()
  const { items, showBuildButton } = useMatterRecordsItems(matterId)
  const {
    open: modalOpen,
    onBuildRecord,
    onOpenChange,
    onContinue,
    onRecordTypeSelected,
    onViewExistingRecord,
    existingRecordTypes,
  } = useBuildMatterRecordModal(onNavigate)

  // Selecting an item closes the menu on its own, but a back-button navigation does not.
  useEffect(() => setOpen(false), [pathname])

  const isAnyItemActive = items.some((item) => item.id === activeItemId)

  return (
    <>
      {/* Not modal: this is navigation chrome, so the rest of the page stays interactive while it is
          open, matching SidebarAccountMenu. */}
      <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <SideNavRailItem label="Matter Records" icon={Clipboard} isActive={isAnyItemActive} />
        </DropdownMenu.Trigger>

        <MenuContent side="right" align="start" sideOffset={4} className="w-[240px]">
          <div className="px-4 pb-0.5 pt-2">
            <span className="font-rethink-sans text-xs font-medium uppercase text-text-tertiary">Matter Records</span>
          </div>

          <MenuItemGroup className="pt-1">
            {items.map((item) => {
              const isActive = activeItemId === item.id
              const state = getSideNavItemStateClassNames(isActive)

              return (
                <DropdownMenu.Item key={item.id} asChild onSelect={onNavigate}>
                  <Link
                    to={item.href}
                    onClick={() => {
                      trackSidebarMatterRecordOpened(trackEvent, matterId, item.id)
                      trackSidebarLinkClicked(trackEvent, item.id, true)
                    }}
                    className={clsx(kFlyoutItemLayout, state.containerClassName)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.icon && <item.icon className={clsx('h-4 w-4 shrink-0', state.iconClassName)} aria-hidden />}
                    <span className={clsx('flex-1', state.labelClassName)}>{item.label}</span>
                    {item.showBetaTag && (
                      <Tag variant="filled" color="Black" size="2xs">
                        beta
                      </Tag>
                    )}
                  </Link>
                </DropdownMenu.Item>
              )
            })}

            {showBuildButton && (
              <DropdownMenu.Item
                className={clsx(kFlyoutItemLayout, kInactiveItemStyle.containerClassName)}
                onSelect={() => {
                  setOpen(false)
                  onBuildRecord()
                }}
              >
                <Plus className={clsx('h-4 w-4 shrink-0', kInactiveItemStyle.iconClassName)} aria-hidden />
                <span className={clsx('flex-1', kInactiveItemStyle.labelClassName)}>Build a new record</span>
              </DropdownMenu.Item>
            )}
          </MenuItemGroup>
        </MenuContent>
      </DropdownMenu.Root>

      <BuildMatterRecordModal
        open={modalOpen}
        onOpenChange={onOpenChange}
        onContinue={onContinue}
        onRecordTypeSelected={onRecordTypeSelected}
        existingRecordTypes={existingRecordTypes}
        onViewExistingRecord={onViewExistingRecord}
      />
    </>
  )
}
